import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import SortImages from '../SortImages';
import styles from '../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../stores/useProductSelectionStore';
import useMultiOpeningMatStore from '../../stores/useMultiOpeningMatStore';
import { getImageId } from '../../lib/utils';
import { ReactComponent as CartIcon } from '../../images/icons/ProductSelectionPage/carticon.svg';
import { ReactComponent as SelectIcon } from '../../images/icons/ProductSelectionPage/selecticon.svg';
import Modal from '../common/Modals/BaseModal';
import CustomTooltip from '../common/Tooltip';

const ThumbnailItem = ({ photo, handleSelectedPhoto, isSelected, isDraggable, onDragStart, isAssigned}) => {
  const [loaded, setLoaded] = useState(false);
  const handleClick = useCallback(() => {
    handleSelectedPhoto(photo);
  }, [handleSelectedPhoto, photo]);

  const shouldHighlight = isSelected;
  return (
    <div
      className={`picture-element-product-selection ${styles.thumbnailWrapper} ${shouldHighlight ? 'selected' : ''}`} 
      onClick={handleClick}
      role="button"
      // style={{ visibility: loaded ? 'visible' : 'hidden', opacity: isAssigned ? 0.6 : 1 }}
      draggable={isDraggable || false}
      onDragStart={isDraggable ? onDragStart : undefined}
    >
      {photo.attributes?.inCart && (
        <div className="w-layout-grid top-buttons-image-element">
          <div className="cart-symbol">
            <div className="icon small x2 w-embed">
              <CartIcon />
            </div>
          </div>
        </div>
      )}
      <img
        src={photo.attributes?.smallestPhoto || photo.attributes?.photoUrl}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        sizes="90px"
        alt={photo.attributes?.photoFileName}
        className="image-thumbnail"
        draggable={false}
      />
      <div className="info-container-mini">
        <div>{photo.attributes?.photoFileName}</div>
      </div>
    </div>
  );
};

const PhotoThumbnails = () => {
  const [isSelectAllModalOpen, setIsSelectAllModalOpen] = useState(false);
  const { id: orderId } = useParams();
  const { photos: storePhotos, selectedPhoto, selectAllPhotos, setSelectAllPhotos, photosLoadingMessage, handleSelectedPhoto, handleChangePhotoOrder, productType} = useProductSelectionStore();
  const { openingAssignments } = useMultiOpeningMatStore();
  const photos = storePhotos;

  const isMultiOpening = productType === 'multi-opening';
  const canSelectAll = ['prints', 'matted-prints', 'mounted-prints'].includes(productType);

  const handleSelectAll = () => {
    setIsSelectAllModalOpen(true);
  };

  const confirmSelectAll = () => {
    setSelectAllPhotos(true);
    setIsSelectAllModalOpen(false);
  };

  const selectedPhotoId = useMemo(
    () => getImageId(selectedPhoto),
    [selectedPhoto]
  );

  const assignedGuids = useMemo(() => {
    if (!isMultiOpening) return null;
    return new Set(
      Object.values(openingAssignments)
        .filter(Boolean)
        .map((p) => p.attributes?.guid),
    );
  }, [isMultiOpening, openingAssignments]);

  const buildDragStart = useCallback((photo) => (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify(photo));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  if (photosLoadingMessage) return null;

  return (
    <>
      <Modal
        isOpen={isSelectAllModalOpen}
        onClose={confirmSelectAll}
        closeLabel="OK"
        onCancel={() => {
          setSelectAllPhotos(false);
          setIsSelectAllModalOpen(false);
        }}
      >
        <p>
          Using the Select All feature disables the crop tool. When using the Select All feature all files need to be pre-cropped before uploading to our system, otherwise they will be cropped down the middle of the file.
        </p>
      </Modal>

      <div className={styles['side-controls-product-selection']}>
        <div className="w-layout-grid grid-44">
          <SortImages
            handleImage={(newImages) => handleChangePhotoOrder(newImages)}
            images={photos}
            orderId={orderId}
            productType={productType}
          />
          {canSelectAll && (
            <CustomTooltip title="Select All Photos">
              <div
                className="rotate-button div-block-169-copy ghost"
                role="button"
                tabIndex={0}
                aria-label="Select all photos"
                aria-pressed={selectAllPhotos}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectAllPhotos ? '#000' : undefined,
                  color: selectAllPhotos ? '#fff' : undefined,
                }}
                onClick={handleSelectAll}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelectAll();
                  }
                }}
              >
                <div className="icon small w-embed">
                  <SelectIcon />
                </div>
              </div>
            </CustomTooltip>
          )}
        </div>
      </div>

      <div 
        className={`${styles['bottom-controls-product-selection']} ${styles.bottomControlsMarginTop}`}
      >
        <div className={styles['bottom-controls-product-selection-grid']}>
          {photos.length > 0 &&
            photos.map((photo) => (
              <ThumbnailItem
                key={getImageId(photo) || photo.id || photo.attributes?.guid}
                photo={photo}
                handleSelectedPhoto={handleSelectedPhoto}
                isSelected={selectAllPhotos || getImageId(photo) === selectedPhotoId}
                isDraggable={isMultiOpening}
                onDragStart={isMultiOpening ? buildDragStart(photo) : undefined}
                isAssigned={isMultiOpening ? assignedGuids.has(photo.attributes?.guid) : false}
              />
            ))}
        </div>
      </div>
    </>
  );
};

export default PhotoThumbnails;
